/**
 * ClubAdminClubs — full club CRUD admin · list + edit + add + delete + image.
 *
 * Path: /club/admin/clubs · /club/admin/clubs/:id_short
 * Auth: shares /mira/console (lc_console_token).
 *
 * FIR-20260611-001 · Phase 4 (founder priority A)
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useConsoleAuth } from "@/lib/console-auth";
import ClubLayout from "./ClubLayout";
import * as A from "./adminApi";
import * as M from "./memberApi";

const TYPES = [
  "City & Social", "Gymkhana", "Golf", "Sports & Recreation",
  "Country Club", "Cosmopolitan", "Turf & Equestrian", "Yacht & Sailing",
];
const LOCAL_SCOPES = ["same_city", "same_metro"];
const METRO_GROUPS = ["", "Delhi", "Mumbai", "Hyderabad", "Bengaluru", "Chennai", "Kolkata", "Pune"];

/* ─── List view ─────────────────────────────────────────── */

export function ClubAdminClubsList() {
  const { user, loading: authLoading, logout } = useConsoleAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ country: "", type: "", offerable: "", needs_review: "" });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/mira/console/login?return=/club/admin/clubs", { replace: true });
      return;
    }
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  async function fetchList() {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (filter.country) params.country = filter.country;
      if (filter.type) params.type = filter.type;
      if (filter.offerable !== "") params.offerable = filter.offerable;
      if (filter.needs_review !== "") params.needs_review = filter.needs_review;
      const r = await A.listClubs(params);
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const countries = useMemo(() => {
    const c = new Set(); items.forEach((x) => x.country && c.add(x.country));
    return Array.from(c).sort();
  }, [items]);

  if (authLoading || !user) {
    return (
      <ClubLayout member={null}>
        <main className="wrap"><div className="loading">Checking access…</div></main>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout member={{ first: user.first || "Admin", last: user.last || "" }} onSignOut={logout}>
      <main className="wrap" style={{ paddingTop: 30, paddingBottom: 80 }}>
        <section className="hero" style={{ padding: "16px 0 24px" }}>
          <div className="kicker">Club Social® · Admin · Clubs</div>
          <h1 className="serif" style={{ fontSize: "clamp(28px, 4vw, 44px)" }}>
            Manage the network
          </h1>
          <p>Add · edit · delete · upload images · set domicile + agreement. Edits write to live MongoDB and survive every redeploy.</p>
          <div style={{ display: "flex", gap: 16, marginTop: 18, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/club/admin/clubs/new" className="btn btn-primary"
                  style={{ padding: "10px 22px", textDecoration: "none" }} data-testid="admin-add-club-btn">
              + Add club
            </Link>
            <label className="btn btn-ghost" style={{ cursor: "pointer", padding: "10px 16px" }} data-testid="admin-club-import-label">
              ↑ Import CSV
              <input type="file" accept=".csv,text/csv" style={{ display: "none" }}
                     onChange={async (e) => {
                       const f = e.target.files?.[0]; if (!f) return;
                       try {
                         const r = await M.importClubsCsv(f);
                         alert(`Import done\nAdded: ${r.added}\nUpdated: ${r.updated}\nSkipped protected fields: ${r.skipped_protected_fields}\nErrors: ${r.errors}`);
                         fetchList();
                       } catch (err) {
                         alert(err.response?.data?.detail || err.message);
                       }
                       e.target.value = "";
                     }}
                     data-testid="admin-club-import-input" />
            </label>
            <a href={M.exportClubsCsvUrl(filter.offerable)} className="btn btn-ghost"
               style={{ padding: "10px 16px", textDecoration: "none" }} data-testid="admin-club-export-btn">
              ↓ Export CSV
            </a>
            <a href={M.exportClubsXlsxUrl(filter.offerable)} className="btn btn-ghost"
               style={{ padding: "10px 16px", textDecoration: "none" }} data-testid="admin-club-export-xlsx-btn">
              ↓ Export Excel (with card HTML)
            </a>
            <Link to="/club/admin/members" style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
              → Members
            </Link>
            <Link to="/club/admin/requests" style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
              → Visit requests
            </Link>
            <Link to="/club" style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
              → Member directory
            </Link>
          </div>
        </section>

        <section className="panel" style={{ position: "static", marginTop: 0 }}>
          <div className="search">
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") fetchList(); }}
                   placeholder="Search name / city / country · Enter to apply"
                   data-testid="admin-search-input" />
          </div>
          <div className="filters">
            <div className="frow">
              <span className="flabel">Country</span>
              <select className="country-sel" value={filter.country}
                      onChange={(e) => setFilter((f) => ({ ...f, country: e.target.value }))}>
                <option value="">All countries</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="flabel">Type</span>
              <select className="country-sel" value={filter.type}
                      onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}>
                <option value="">All types</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="flabel">Status</span>
              <select className="country-sel" value={filter.offerable}
                      onChange={(e) => setFilter((f) => ({ ...f, offerable: e.target.value }))}>
                <option value="">Any</option>
                <option value="true">Offerable only</option>
                <option value="false">Not offerable</option>
              </select>
              <select className="country-sel" value={filter.needs_review}
                      onChange={(e) => setFilter((f) => ({ ...f, needs_review: e.target.value }))}>
                <option value="">Any review</option>
                <option value="true">Needs review</option>
                <option value="false">Verified</option>
              </select>
              <button className="chip on" onClick={fetchList} data-testid="admin-apply-filters">Apply</button>
            </div>
          </div>
        </section>

        {error && <div className="empty"><div className="serif">Error</div>{error}</div>}

        {loading ? (
          <div className="loading">Loading clubs…</div>
        ) : (
          <div style={{
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 14, overflow: "hidden", boxShadow: "var(--shadow)", marginTop: 14,
          }}>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)", fontSize: 13 }}>
              Showing <b style={{ color: "var(--ink)" }}>{items.length}</b> of <b style={{ color: "var(--ink)" }}>{total.toLocaleString()}</b> matches
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid="admin-clubs-table">
                <thead>
                  <tr style={{ background: "rgba(35,48,42,0.04)", textAlign: "left" }}>
                    {["Club", "Location", "Type", "Offerable", "Review", "Actions"].map((h) => (
                      <th key={h} style={{ padding: "11px 14px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id_short} data-testid={`admin-club-row-${c.id_short}`}
                        style={{ borderTop: "1px solid var(--line-2)" }}>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 11.5, color: "var(--teal-d)", marginTop: 2, fontFamily: "ui-monospace, Menlo, monospace" }}>{c.id_short}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {c.city || "—"}{c.country ? `, ${c.country}` : ""}
                      </td>
                      <td style={{ padding: "12px 14px" }}>{c.type || "—"}</td>
                      <td style={{ padding: "12px 14px" }}>
                        {c.offerable ? (
                          <span style={{ background: "rgba(127,174,60,0.18)", color: "#3d6010", padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>YES</span>
                        ) : (
                          <span style={{ background: "rgba(160,122,78,0.18)", color: "#7a532c", padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>NO</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {c.needs_review ? (
                          <span style={{ background: "rgba(180,68,31,0.16)", color: "#7a2c12", padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>NEEDS REVIEW</span>
                        ) : (
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>verified</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Link to={`/club/admin/clubs/${c.id_short}`}
                              data-testid={`admin-edit-${c.id_short}`}
                              style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2 }}>
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </ClubLayout>
  );
}

/* ─── Edit / Create view ─────────────────────────────────── */

const NEW_DEFAULTS = {
  name: "", city: "", state: "", country: "", type: "",
  am: [], short: "", long: "", addr: "", postcode: "", hours: "", dress: "",
  agreement: { status: "inactive" }, offerable: false,
  excludes_locals: false, local_scope: "same_city", metro_group: "",
};

export function ClubAdminClubEdit() {
  const { id_short } = useParams();
  const isNew = id_short === "new";
  const { user, loading: authLoading, logout } = useConsoleAuth();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [draft, setDraft] = useState(isNew ? NEW_DEFAULTS : null);
  const [extraAmenities, setExtraAmenities] = useState([]);
  const [canonical, setCanonical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/mira/console/login?return=/club/admin/clubs${isNew ? "/new" : "/" + id_short}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const am = await A.listAmenities();
        setCanonical(am.canonical || []);
        setExtraAmenities(am.extra || []);
        if (!isNew) {
          const c = await A.getClub(id_short);
          setClub(c);
          setDraft({
            name: c.name || "", city: c.city || "", state: c.state || "", country: c.country || "",
            type: c.type || "", am: c.am || [], short: c.short || "", long: c.long || "",
            addr: c.addr || "", postcode: c.postcode || "", hours: c.hours || "", dress: c.dress || "",
            agreement: c.agreement || { status: "inactive" },
            offerable: !!c.offerable,
            excludes_locals: !!c.excludes_locals,
            local_scope: c.local_scope || "same_city",
            metro_group: c.metro_group || "",
          });
        }
      } catch (e) {
        setMsg({ ok: false, text: e.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, id_short]);

  function setField(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
  }
  function toggleAmenity(name) {
    setDraft((d) => {
      const has = (d.am || []).includes(name);
      return { ...d, am: has ? d.am.filter((a) => a !== name) : [...(d.am || []), name] };
    });
  }
  async function addNewAmenity() {
    const v = window.prompt("New amenity name (will be selectable across all clubs + as a member filter chip):");
    if (!v || !v.trim()) return;
    await A.addAmenity(v.trim());
    setExtraAmenities((e) => [...e, v.trim()]);
  }

  async function save() {
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const r = await A.createClub(draft);
        setMsg({ ok: true, text: "Club created." });
        setTimeout(() => navigate(`/club/admin/clubs/${r.id_short}`, { replace: true }), 600);
      } else {
        const r = await A.patchClub(id_short, draft);
        setClub(r.club);
        setMsg({ ok: true, text: `Saved · ${r.changed} field${r.changed === 1 ? "" : "s"} updated.` });
      }
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function uploadImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true); setMsg(null);
    try {
      const r = await A.uploadClubImage(id_short, file);
      setClub((c) => ({ ...c, img: r.url }));
      setMsg({ ok: true, text: "Image uploaded to Cloudinary." });
    } catch (e2) {
      setMsg({ ok: false, text: e2.response?.data?.detail || e2.message });
    } finally { setSaving(false); }
  }

  async function removeImage() {
    if (!window.confirm("Remove the image? The member surface will fall back to the monogram tile.")) return;
    setSaving(true); setMsg(null);
    try {
      await A.deleteClubImage(id_short);
      setClub((c) => ({ ...c, img: null }));
      setMsg({ ok: true, text: "Image removed." });
    } catch (e2) {
      setMsg({ ok: false, text: e2.response?.data?.detail || e2.message });
    } finally { setSaving(false); }
  }

  async function softDelete() {
    const reason = window.prompt("Soft-delete reason (e.g. 'agreement terminated'):");
    if (reason === null) return;
    setSaving(true); setMsg(null);
    try {
      await A.deleteClub(id_short, { reason: reason || "admin_soft_delete" });
      setMsg({ ok: true, text: "Club soft-deleted (active:false, offerable:false). Reversible." });
      setTimeout(() => navigate("/club/admin/clubs", { replace: true }), 800);
    } catch (e2) {
      setMsg({ ok: false, text: e2.response?.data?.detail || e2.message });
    } finally { setSaving(false); }
  }

  async function hardDelete() {
    if (!window.confirm("PERMANENT delete — this cannot be undone. Continue?")) return;
    if (window.prompt('Type "DELETE" to confirm:') !== "DELETE") return;
    setSaving(true);
    try {
      await A.deleteClub(id_short, { hard: true });
      navigate("/club/admin/clubs", { replace: true });
    } catch (e2) {
      setMsg({ ok: false, text: e2.response?.data?.detail || e2.message });
      setSaving(false);
    }
  }

  if (authLoading || !user || loading) {
    return (
      <ClubLayout member={null}>
        <main className="wrap"><div className="loading">Loading…</div></main>
      </ClubLayout>
    );
  }
  if (!draft) {
    return (
      <ClubLayout member={null}>
        <main className="wrap"><div className="empty"><div className="serif">Club not found</div></div></main>
      </ClubLayout>
    );
  }

  const allAmenities = [...canonical, ...extraAmenities];

  return (
    <ClubLayout member={{ first: user.first || "Admin", last: user.last || "" }} onSignOut={logout}>
      <main className="wrap" style={{ paddingTop: 24, paddingBottom: 80 }}>
        <section className="hero" style={{ padding: "10px 0 18px" }}>
          <Link to="/club/admin/clubs" style={{ color: "var(--teal-d)", fontSize: 13, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>← back to clubs</Link>
          <h1 className="serif" style={{ fontSize: "clamp(24px, 3.4vw, 36px)", marginTop: 6 }}>
            {isNew ? "Add a new club" : (draft.name || "Edit club")}
          </h1>
          {!isNew && club && (
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, fontFamily: "ui-monospace, Menlo, monospace" }}>
              id {id_short} · {club.founder_edited_fields?.length ? `${club.founder_edited_fields.length} founder-edited field${club.founder_edited_fields.length === 1 ? "" : "s"} (protected)` : "no founder edits yet"}
            </div>
          )}
        </section>

        {msg && (
          <div style={{
            padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 13.5,
            background: msg.ok ? "rgba(127,174,60,0.16)" : "rgba(180,68,31,0.16)",
            color: msg.ok ? "#3d6010" : "#7a2c12",
            border: `1px solid ${msg.ok ? "rgba(127,174,60,0.4)" : "rgba(180,68,31,0.4)"}`,
          }} data-testid="admin-msg">{msg.text}</div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 22 }}>
          <div className="form-card" style={cardStyle()}>
            <Section title="Identity & Location">
              <FieldRow>
                <Field label="Name *" value={draft.name} onChange={(v) => setField("name", v)} testid="admin-field-name" />
              </FieldRow>
              <FieldRow>
                <Field label="City *" value={draft.city} onChange={(v) => setField("city", v)} testid="admin-field-city" />
                <Field label="State / Region" value={draft.state} onChange={(v) => setField("state", v)} />
              </FieldRow>
              <FieldRow>
                <Field label="Country *" value={draft.country} onChange={(v) => setField("country", v)} testid="admin-field-country" />
                <Field label="Postcode" value={draft.postcode} onChange={(v) => setField("postcode", v)} />
              </FieldRow>
              <FieldRow>
                <Field label="Address" value={draft.addr} onChange={(v) => setField("addr", v)} full />
              </FieldRow>
            </Section>

            <Section title="Presentation">
              <FieldRow>
                <SelectField label="Type" value={draft.type} options={TYPES} onChange={(v) => setField("type", v)} testid="admin-field-type" />
                <Field label="Opening hours" value={draft.hours} onChange={(v) => setField("hours", v)} />
              </FieldRow>
              <FieldRow>
                <Field label="Short description" value={draft.short} onChange={(v) => setField("short", v)} full multiline rows={2} />
              </FieldRow>
              <FieldRow>
                <Field label="Long description" value={draft.long} onChange={(v) => setField("long", v)} full multiline rows={5} />
              </FieldRow>
              <FieldRow>
                <Field label="Dress code" value={draft.dress} onChange={(v) => setField("dress", v)} full multiline rows={3} />
              </FieldRow>
            </Section>

            <Section title={`Amenities (${(draft.am || []).length} selected)`}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {allAmenities.map((a) => {
                  const on = (draft.am || []).includes(a);
                  return (
                    <button key={a} type="button"
                            className={`svc-chip${on ? " on" : ""}`}
                            data-testid={`admin-am-${a.replace(/[^a-z]/gi, "")}`}
                            onClick={() => toggleAmenity(a)}>
                      {a}
                    </button>
                  );
                })}
                <button type="button" className="svc-chip" style={{ borderStyle: "dashed" }}
                        onClick={addNewAmenity} data-testid="admin-add-amenity-btn">
                  + new amenity
                </button>
              </div>
            </Section>

            <Section title="Domicile / §6 verification">
              <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -4 }}>
                Editing any field below clears <b>needs_review</b> and stamps <code>excludes_locals_source = &quot;verified&quot;</code>.
              </p>
              <FieldRow>
                <Toggle label="Excludes locals (§6)"
                        value={!!draft.excludes_locals}
                        onChange={(v) => setField("excludes_locals", v)}
                        testid="admin-field-excludes-locals" />
                <SelectField label="Local scope" value={draft.local_scope} options={LOCAL_SCOPES}
                             onChange={(v) => setField("local_scope", v)} />
                <SelectField label="Metro group (if same_metro)"
                             value={draft.metro_group || ""} options={METRO_GROUPS}
                             onChange={(v) => setField("metro_group", v)} />
              </FieldRow>
            </Section>

            <Section title="Agreement / Offerable (§6.5)">
              <FieldRow>
                <Toggle label="Offerable (visible to members)"
                        value={!!draft.offerable}
                        onChange={(v) => setField("offerable", v)}
                        testid="admin-field-offerable" />
                <SelectField label="Agreement status"
                             value={draft.agreement?.status || "inactive"}
                             options={["active", "inactive"]}
                             onChange={(v) => setField("agreement", { ...(draft.agreement || {}), status: v })} />
              </FieldRow>
            </Section>

            <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}
                      data-testid="admin-save-btn">
                {saving ? "Saving…" : isNew ? "Create club" : "Save changes"}
              </button>
              {!isNew && (
                <>
                  <button className="btn btn-ghost" onClick={softDelete} disabled={saving} data-testid="admin-softdelete-btn">
                    Soft-delete
                  </button>
                  <button className="btn btn-ghost" onClick={hardDelete} disabled={saving}
                          style={{ borderColor: "#b4441f", color: "#7a2c12" }}
                          data-testid="admin-harddelete-btn">
                    Hard delete
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Sidebar: image + meta */}
          <div className="form-card" style={cardStyle()}>
            <Section title="Image">
              {(club?.img || draft?.img) ? (
                <div>
                  <img src={club?.img || draft?.img} alt={draft.name}
                       style={{ width: "100%", borderRadius: 10, border: "1px solid var(--line)", aspectRatio: "16/10", objectFit: "cover" }}
                       data-testid="admin-club-image" />
                  {!isNew && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <label className="btn btn-ghost" style={{ cursor: "pointer", flex: 1, textAlign: "center", padding: "10px 14px", fontSize: 13 }}>
                        Replace
                        <input type="file" accept="image/*" style={{ display: "none" }}
                               onChange={uploadImage} data-testid="admin-image-replace" />
                      </label>
                      <button className="btn btn-ghost" onClick={removeImage}
                              style={{ padding: "10px 14px", fontSize: 13, borderColor: "#b4441f", color: "#7a2c12" }}
                              data-testid="admin-image-remove">
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{
                    width: "100%", aspectRatio: "16/10", borderRadius: 10,
                    background: "linear-gradient(135deg, #2E9D9D, #1c4a3f)",
                    color: "#fff", display: "grid", placeItems: "center",
                    fontFamily: "'Fraunces', serif", fontSize: 48, fontWeight: 500,
                  }}>{(draft.name?.[0] || "C").toUpperCase()}</div>
                  {!isNew ? (
                    <label className="btn btn-primary" style={{ cursor: "pointer", display: "block", textAlign: "center", marginTop: 10, padding: "10px 14px", fontSize: 13 }}>
                      Upload image
                      <input type="file" accept="image/*" style={{ display: "none" }}
                             onChange={uploadImage} data-testid="admin-image-upload" />
                    </label>
                  ) : (
                    <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 10 }}>
                      Save the club first, then come back to upload an image.
                    </p>
                  )}
                </div>
              )}
            </Section>

            {!isNew && club && (
              <Section title="Audit">
                <Meta k="id_short" v={club.id_short} />
                <Meta k="Founder edits" v={(club.founder_edited_fields || []).length} />
                {club.founder_edited_at && (
                  <Meta k="Last edited" v={new Date(club.founder_edited_at).toLocaleString()} />
                )}
                {club.clubs_clean_merged_at && (
                  <Meta k="Bundle merge" v={new Date(club.clubs_clean_merged_at).toLocaleString()} />
                )}
              </Section>
            )}
          </div>
        </div>
      </main>
    </ClubLayout>
  );
}

/* ─── Micro components ──────────────────────────────────── */

function cardStyle() {
  return {
    background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 14, padding: "20px 22px",
    boxShadow: "var(--shadow)",
  };
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h4 style={{
        fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
        color: "var(--muted)", fontWeight: 700, marginBottom: 12,
      }}>{title}</h4>
      {children}
    </div>
  );
}

function FieldRow({ children }) {
  return <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>{children}</div>;
}

function Field({ label, value, onChange, full, multiline, rows = 1, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: full ? "1 1 100%" : "1 1 200px", minWidth: 0 }}>
      <label style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, marginBottom: 4 }}>
        {label}
      </label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
                  style={inputStyle()} data-testid={testid} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
               style={inputStyle()} data-testid={testid} />
      )}
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 200px" }}>
      <label style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, marginBottom: 4 }}>
        {label}
      </label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
              style={inputStyle()} data-testid={testid}>
        {!options.includes("") && <option value=""></option>}
        {options.map((o) => <option key={o} value={o}>{o || "(none)"}</option>)}
      </select>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 200px" }}>
      <label style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, marginBottom: 4 }}>
        {label}
      </label>
      <button type="button"
              onClick={() => onChange(!value)}
              data-testid={testid}
              style={{
                ...inputStyle(),
                background: value ? "rgba(46,157,157,0.15)" : "var(--bg)",
                borderColor: value ? "var(--teal)" : "var(--line)",
                color: value ? "var(--teal-d)" : "var(--ink)",
                fontWeight: 600, cursor: "pointer",
                textAlign: "left",
              }}>
        {value ? "✓ Yes" : "Off"}
      </button>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--line-2)", fontSize: 13 }}>
      <span style={{ color: "var(--muted)" }}>{k}</span>
      <b style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>{String(v)}</b>
    </div>
  );
}

function inputStyle() {
  return {
    border: "1px solid var(--line)",
    borderRadius: 10,
    background: "var(--bg)",
    padding: "10px 12px",
    fontFamily: "'Archivo', sans-serif",
    fontSize: 14,
    color: "var(--ink)",
    width: "100%",
    boxSizing: "border-box",
  };
}
